/**
 * docsTree — pure hierarchy math for Docs nesting. No Firebase, no TipTap.
 *
 * MODEL: each doc carries `parentId` (null at root) plus a DENORMALIZED
 * `ancestors[]` — the ids from root down to the parent, exclusive of self:
 *
 *   root            parentId: null   ancestors: []
 *   └ child         parentId: root   ancestors: [root]
 *     └ grandchild  parentId: child  ancestors: [root, child]
 *
 * Why denormalize: breadcrumbs and "everything under X" become one array read
 * or one array-contains query — no recursive fetching. The cost is that MOVING a
 * subtree must rewrite every descendant's ancestors[], which is what
 * `moveUpdates` computes (DocsService applies it as a single batch).
 *
 * `ancestors[]` is derived state — always recompute it from parentId rather than
 * editing it directly, or the two representations drift and breadcrumbs start
 * lying about where a doc lives.
 */

/** Index docs by id for O(1) parent lookups. */
export function indexById(docs) {
	return new Map((docs || []).map((d) => [d.id, d]));
}

/** The ancestors[] a doc should have if parented under `parentId`. */
export function ancestorsFor(parentId, byId) {
	if (!parentId) return [];
	const parent = byId.get(parentId);
	// An unknown parent is treated as root rather than throwing: a doc whose
	// parent was hard-deleted should still render somewhere, not vanish.
	if (!parent) return [];
	return [...(parent.ancestors || []), parent.id];
}

/** Every doc beneath `id`, at any depth. */
export function descendantsOf(docs, id) {
	return (docs || []).filter((d) => (d.ancestors || []).includes(id));
}

/** Direct children only. */
export function childrenOf(docs, id) {
	return (docs || []).filter((d) => (d.parentId || null) === (id || null));
}

/**
 * Whether `docId` may be re-parented under `newParentId`.
 *
 * The load-bearing guard is the cycle check: dropping a doc into its own
 * descendant would detach that whole branch from the tree — it would still exist
 * in Firestore but be unreachable from any root, and its ancestors[] would
 * reference itself. Blocked here so neither the UI nor the service can do it.
 */
export function canMove(docId, newParentId, byId) {
	if (!docId) return { ok: false, reason: 'no-doc' };
	const target = newParentId || null;
	if (target === null) return { ok: true };
	if (target === docId) return { ok: false, reason: 'self' };

	const parent = byId.get(target);
	if (!parent) return { ok: false, reason: 'missing-parent' };
	if ((parent.ancestors || []).includes(docId)) {
		return { ok: false, reason: 'cycle' };
	}
	return { ok: true };
}

/**
 * The full write-set for a move: the doc itself, plus every descendant whose
 * ancestors[] shifts. Returns [] when the move is a no-op, so a caller can skip
 * a pointless batch.
 *
 * Each descendant keeps its position RELATIVE to the moved doc — we splice the
 * new prefix onto the tail of its existing path rather than recomputing the
 * whole subtree, so deep structure survives the move intact.
 */
export function moveUpdates(docId, newParentId, docs) {
	const byId = indexById(docs);
	const doc = byId.get(docId);
	if (!doc) return [];

	const target = newParentId || null;
	if ((doc.parentId || null) === target) return []; // already there

	const check = canMove(docId, target, byId);
	if (!check.ok) return [];

	const newAncestors = ancestorsFor(target, byId);
	const updates = [{ id: docId, parentId: target, ancestors: newAncestors }];

	// Each descendant's path = [new path to doc] + [doc] + [its path below doc].
	const oldPrefixLen = (doc.ancestors || []).length + 1; // through `doc` itself
	for (const d of descendantsOf(docs, docId)) {
		const tail = (d.ancestors || []).slice(oldPrefixLen);
		updates.push({ id: d.id, ancestors: [...newAncestors, docId, ...tail] });
	}

	return updates;
}

/**
 * When a doc is removed, its children are PROMOTED to its parent rather than
 * deleted — losing a subtree because someone tidied one page would be a
 * destructive surprise. Returns the write-set for the promotion.
 */
export function promoteChildrenUpdates(docId, docs) {
	const byId = indexById(docs);
	const doc = byId.get(docId);
	if (!doc) return [];
	const grandparent = doc.parentId || null;

	const updates = [];
	for (const child of childrenOf(docs, docId)) {
		updates.push(...moveUpdatesAfterRemoval(child.id, grandparent, docs, docId));
	}
	return updates;
}

/* Like moveUpdates, but computed as if `removedId` is already gone — used by
   promoteChildrenUpdates, where the cycle check would otherwise consult the
   very doc being deleted. */
function moveUpdatesAfterRemoval(docId, newParentId, docs, removedId) {
	const remaining = docs.filter((d) => d.id !== removedId);
	return moveUpdates(docId, newParentId, remaining);
}

/**
 * Plan a subtree duplication: given the docs being copied and a map from old id
 * to freshly-minted new id, produce each copy's parentId and ancestors[].
 *
 * The rule is a single substitution: any id INSIDE the copied subtree is
 * remapped, any id ABOVE it is left alone. That's what keeps the copy attached
 * where the original lived while its internal structure points at the new docs
 * rather than the originals — get this wrong and the duplicate's children hang
 * off the ORIGINAL parent, silently corrupting the source tree.
 *
 * `sourceId` is the root of the copy; it keeps the original's parent, so the
 * duplicate lands as a sibling of what it was copied from.
 */
export function duplicateMappings(subtree, sourceId, idMap) {
	return (subtree || []).map((d) => {
		const remap = (id) => idMap.get(id) || id;
		const isRoot = d.id === sourceId;
		return {
			fromId: d.id,
			newId: idMap.get(d.id),
			isRoot,
			// The root copy stays beside the original; descendants re-point at
			// their copied parent.
			parentId: isRoot ? d.parentId || null : remap(d.parentId),
			ancestors: (d.ancestors || []).map(remap),
		};
	});
}

/** Breadcrumb trail (root → parent), skipping ids no longer present. */
export function breadcrumbFor(doc, byId) {
	if (!doc) return [];
	return (doc.ancestors || [])
		.map((id) => byId.get(id))
		.filter(Boolean)
		.map((d) => ({ id: d.id, title: d.title }));
}

/**
 * Build a nested tree for rendering. Docs whose parent is missing (deleted, or
 * simply not in this page of results) surface as roots — never dropped, because
 * a doc you can't see is a doc you can't recover.
 */
export function buildTree(docs, { sort } = {}) {
	const list = docs || [];
	const byId = indexById(list);
	const nodes = new Map(list.map((d) => [d.id, { ...d, children: [] }]));
	const roots = [];

	for (const node of nodes.values()) {
		const pid = node.parentId || null;
		const parent = pid ? nodes.get(pid) : null;
		if (parent && byId.has(pid)) parent.children.push(node);
		else roots.push(node);
	}

	const cmp =
		sort ||
		((a, b) => {
			if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
			return (a.title || '').localeCompare(b.title || '');
		});

	const sortRec = (arr) => {
		arr.sort(cmp);
		arr.forEach((n) => sortRec(n.children));
		return arr;
	};

	return sortRec(roots);
}

/** Flatten a tree to rows with depth, honoring a collapsed-id set. */
export function flattenTree(tree, collapsed = new Set(), depth = 0, out = []) {
	for (const node of tree) {
		out.push({ ...node, depth, hasChildren: node.children.length > 0 });
		if (node.children.length && !collapsed.has(node.id)) {
			flattenTree(node.children, collapsed, depth + 1, out);
		}
	}
	return out;
}
